<template>
  <div>
    <QPageContainer>
      <QPage>
        <section>
          <Upfront :demand-recap="demandRecap" :has-active-subscription="hasActiveSubscription" />
        </section>
      </QPage>
    </QPageContainer>

    <CardsListModal
      v-if="paymentModalOpened"
      v-model:opened="paymentModalOpened"
      :cards-collection="cardsCollection"
      @on-add-card="onAddCard"
      @on-select-and-pay="onSelectAndPay"
    />

    <QFooter class="bg-transparent">
      <div>
        <!-- activate with active subscription -->
        <template v-if="hasActiveSubscription">
          <QBtn
            color="secondary"
            class="full-width btn-navigation"
            no-caps
            :label="$t('ETAB.PAGES.DEMANDS.CREATION.@UPFRONT_PUBLISH')"
            @click="activateWithSubscription"
          />
        </template>
        <!-- pay or decline payment -->
        <template v-else>
          <QBtn
            color="white"
            text-color="primary"
            class="btn-navigation-half"
            no-caps
            :label="$t('ETAB.PAGES.DEMANDS.CREATION.@UPFRONT_IGNORE')"
            @click="onCancelDemand()"
          />
          <QBtn
            color="secondary"
            class="btn-navigation-half"
            no-caps
            :label="$t('ETAB.PAGES.DEMANDS.CREATION.@UPFRONT_ACTION')"
            @click="onPayAction()"
          />
        </template>
      </div>
    </QFooter>
  </div>
</template>

<script>
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { RecruitmentsService } from "@core/services";
  import { GETTER_ACTIVATION_GET_DEMAND_RECAP, GETTER_SUBSCRIPTION_HAS_SUBSCRIPTION } from "@etab/stores/types";
  import { useActivationStore } from "@etab/stores/activation";
  import { useSubscriptionStore } from "@etab/stores/subscription";
  import { mapState } from "pinia";
  import { paymentMixin } from "@etab/mixins";
  import { CardsCollectionModel } from "@core/models";
  import { ETAB_ROUTES } from "@etab/config";

  import CardsListModal from "@etab/components/payment/CardsList.modal.vue";
  import Upfront from "@etab/components/payment/Upfront.component.vue";

  export default {
    components: {
      CardsListModal,
      Upfront,
    },
    mixins: [paymentMixin],
    data() {
      return {
        paymentModalOpened: false,
        cardsCollection: new CardsCollectionModel(),
      };
    },
    computed: {
      ...mapState(useActivationStore, {
        demandRecap: GETTER_ACTIVATION_GET_DEMAND_RECAP,
      }),
      ...mapState(useSubscriptionStore, {
        hasActiveSubscription: GETTER_SUBSCRIPTION_HAS_SUBSCRIPTION,
      }),
      demandId() {
        return this.$route.params.demandId;
      },
    },
    methods: {
      openPaymentModal() {
        this.paymentModalOpened = true;
      },
      closePaymentModal() {
        this.paymentModalOpened = false;
      },
      async onCancelDemand() {
        this.$q
          .dialog({
            title: this.$t("ETAB.PAGES.DEMANDS.CREATION.@UPFRONT_CANCEL_DEMAND_MODAL_TITLE"),
            message: this.$t("ETAB.PAGES.DEMANDS.CREATION.@UPFRONT_CANCEL_DEMAND_MODAL_MESSAGE"),
            ok: {
              label: this.$t("ETAB.PAGES.DEMANDS.CREATION.@UPFRONT_CANCEL_DEMAND_MODAL_OK"),
              color: "primary",
              noCaps: true,
            },
            cancel: {
              label: this.$t("ETAB.PAGES.DEMANDS.CREATION.@UPFRONT_CANCEL_DEMAND_MODAL_NOK"),
              flat: true,
              noCaps: true,
            },
          })
          .onOk(() => this.cancelDemand());
      },
      async cancelDemand() {
        try {
          this.$_startGlobalLoading();
          await RecruitmentsService.cancelDemandAfterCreation({ demandId: this.demandId });
          NotificationsManager.success({ message: "SUCCESS.@CANCEL_DEMAND" });
          this.goToCreation();
        } catch (error) {
          NotificationsManager.contactEtabSupport();
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async onPayAction() {
        try {
          this.$_startGlobalLoading();
          await this.chooseCard();
        } catch (error) {
          NotificationsManager.contactEtabSupport();
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async chooseCard() {
        const cardsCollection = await this.$_getSavedCards();
        if (cardsCollection.hasCards()) {
          this.cardsCollection = cardsCollection;
          this.openPaymentModal();
        } else {
          this.goToAddCardAndPay();
        }
      },
      goToAddCardAndPay() {
        this.$router
          .push({
            name: ETAB_ROUTES.payment.addAndPay.name,
            query: { demandId: this.demandId, redirectTo: ETAB_ROUTES.creationSuccess.name, upfront: 1 },
          })
          .catch((suppressedError) => {});
      },
      onAddCard() {
        this.closePaymentModal();
        this.goToAddCardAndPay();
      },
      async onSelectAndPay({ selectedCard }) {
        try {
          const cardId = selectedCard.getId();
          const demandId = this.demandId;
          const upfront = 1;

          this.$_startGlobalLoading();

          await this.$_payWithCard({ cardId, demandId, upfront });

          this.closePaymentModal();
          this.goToSuccess();
        } catch (error) {
          this.$_handlePaymentError({ error });
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      goToSuccess() {
        // we prevent user to go back to upfront
        this.$router.replace({ name: ETAB_ROUTES.creationSuccess.name });
      },
      goToCreation() {
        // we prevent user to go back to upfront
        this.$router.replace({ name: ETAB_ROUTES.creation.root.name });
      },
      async activateWithSubscription() {
        try {
          this.$_startGlobalLoading();
          const demandId = this.demandId;
          await this.$_unlockWithSubscription({ demandId });
          this.goToSuccess();
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
    },
  };
</script>
